import * as React from "react";
import * as yup from "yup";
import * as Comp from "../../../components";
import { ComponentWithValidatedForm } from "../../../components/validation";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { Utility } from "../../../utils";

interface OwnProps {
    api: Api;
    onComplete: (fromId: number, toTag: M.Tag.TagAdmin) => void;
}

interface OwnState {
    tag?: M.Tag.TagAdmin;
    show: boolean;
}

interface TagMergeData {
    fromId: number;
    toTag: M.Generic.ReactSelectOption | null;
}

class TagMerge implements M.Validation.FormValidationModel<TagMergeData> {
    public data: TagMergeData;
    public rules: yup.Schema<TagMergeData>;

    public constructor() {
        this.data = {
            fromId: 0,
            toTag: null,
        };
        this.rules = yup.object<TagMergeData>().shape({
            fromId: yup.number().min(0, "Invalid $label").label("ID"),
            toTag: Forms.Generic.ReactSelectOptionSchema.nullable(true).required().label("Merge with"),
        });
    }
}

export class TagMergeModal extends ComponentWithValidatedForm<OwnProps, OwnState> {

    private _isMounted = false;
    private _searchTimeout: NodeJS.Timer;

    public constructor(props: any) {
        super(props);

        const formModel = new TagMerge();
        this.state = {
            ...this.getInitialState(formModel),
            show: false,
        };
    }

    public componentDidMount() {
        this._isMounted = true;
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public close() {
        this.setState({ ...this.state, show: false });
    }

    public show(tag: M.Tag.TagAdmin) {
        const formModel = new TagMerge();
        formModel.data.fromId = tag.id;

        this.setState({ ...this.state,
            ...this.getInitialState(formModel),
            tag,
            show: true,
        });
    }

    public render() {
        const tag = this.state.tag;
        const tagName = tag ? tag.name : "";

        return <Comp.Modal.ModalForm
            formComponent={this}
            onCancel={() => this.close()}
            processing={this.state.formProcessing}
            show={this.state.show}
            title={<>Merge {tag ? <em>{tag.name}</em> : ""} tag</>}
        >
            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                onValidSubmit={(model) => this.onSubmit(model)}
                schema={this.state.formModel.rules}
                ref={(ref: any) => this.form = ref}
            >

                <Comp.Editors.FormField label="Tag to remove" name="" compact>
                    <strong>{tagName}</strong>
                    <div className="field-help">
                        Merging will replace <em>{tagName}</em> with the chosen tag below.
                    </div>
                </Comp.Editors.FormField>

                <Comp.Editors.SelectField
                    formComponent={this}
                    compact
                    isAsync
                    name="toTag"
                    select={{
                        // cache: false,
                        arrowRenderer: () => null,
                        ignoreCase: false,
                        loadOptions: (search: string) => this.searchTags(search),
                        optionRenderer: (r: M.Tag.Tag) => <span>{r.name}</span>,
                        placeholder: "Retinablastoma, Macular pucker, etc.",
                        searchPromptText: "Start typing and select from suggested",
                        valueRenderer: (r: M.Tag.Tag) => <span>{r.name}</span>,
                    }}
                />

            </Comp.Validation.ValidatedForm>
        </Comp.Modal.ModalForm>;
    }

    private async onSubmit(model: TagMergeData) {
        if (!this.state.tag)
            return;

        this.toggleProcessing(true);

        const id = model.fromId;
        const toId = model.toTag ? model.toTag.value : 0;
        const result = await this.props.api.put(Api.endpoints.admin.tag.merge.withUrlParams({ id, toId }));

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            this.close();
            this.props.onComplete(model.fromId, result.data);
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result));
        }

        this.toggleProcessing(false);
    }

    private searchTags(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = { queryParams: { search, max: 10 }};
                this.props.api.callApi<M.Tag.Tag[]>(Api.endpoints.tag.tags, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            const options = result.data.map((tag) => Utility.toReactSelectOption(tag, tag.name, tag.id));
                            resolve({ options });
                        }
                        else
                            console.error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminTag/components/TagMergeModal.tsx